import { Component, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { LocalizationService } from 'root/services/localization/localization.service';
import { GlobalConfigService, LogService } from 'root/services';
import { BasicChartData } from "root/components/chart";
import { HttpCancellactionService } from "root/services/http-cancellation/http-cancellation.service";

import { ResourceStringComponent } from 'root/shared/components/localization/resource-string/resource-string.component';
import { MibpChartPieComponent } from 'root/components/chart/mibp-chart-pie/mibp-chart-pie.component';
import { MibpNewPopoverComponent } from 'root/shared/components/popovers/new-popover/new-popover.component';
import { EcommerceRouterLinkDirective } from 'root/directives/ecommerce-router-link/ecommerce-router-link.directive';
import { ClientSideCacheService, CacheScope } from 'root/services/client-side-cache/client-side-cache.service';
import { equipmentsFnGetEquipmentFleetCount } from "root/mibp-openapi-gen/fn/equipments/equipments-fn-get-equipment-fleet-count";
import { MibpApiService } from "root/mibp-openapi-gen/mibp-api.service";

interface FleetCountResult {
  active: number;
  inActive: number;
  onOrder: number;
  count: number;
  deCommisioned: number;
}

@Component({
  selector: "mibp-dashboard-myfleet",
  templateUrl: "./dashboard-myfleet.component.html",
  styleUrls: ["./dashboard-myfleet.component.scss"],
  providers: [HttpCancellactionService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ResourceStringComponent, MibpChartPieComponent, MibpNewPopoverComponent, EcommerceRouterLinkDirective]
})
export class MibpDashboardMyFleetComponent implements OnInit, OnDestroy {
  private mibpApiService = inject(MibpApiService);
  private localizationService = inject(LocalizationService);
  private globalConfig = inject(GlobalConfigService);
  private httpCancellation = inject(HttpCancellactionService);
  private cache = inject(ClientSideCacheService);
  private log = inject(LogService).withPrefix('dashboard-myfleet.component');

  private readonly fleetCountCacheKey = 'dashboard-myfleet-fleetcount';
  private readonly fleetCountCacheDuration = '60 minutes';
  private stopUsingLocalization?: (() => void);

  protected isLoading = signal(false);
  protected hasError = signal(false);
  protected fleetChartData = signal<BasicChartData[] | null>(null);
  protected fleetActiveCount = signal(0);
  protected fleetInActiveCount = signal(0);
  protected fleetOnOrderCount = signal(0);
  protected fleetTotalDetails = signal<string[]>([]);
  protected fleetMachineText = signal<string | null>(null);
  protected isDynamicdashboard = signal(false);

  ngOnInit(): void {
    this.isDynamicdashboard.set(this.globalConfig.activateDynamicDashboard);
    this.stopUsingLocalization = this.localizationService.using(["MyFleet_Machines"], (resourceStrings) => {
      this.fleetMachineText.set(resourceStrings[0]);
      this.update();
    });
  }

  ngOnDestroy(): void {
    this.stopUsingLocalization?.();
  }

  async update(): Promise<void> {
    this.hasError.set(false);
    this.isLoading.set(true);
    this.fleetChartData.set(null);
    this.fleetActiveCount.set(0);
    this.fleetInActiveCount.set(0);
    this.fleetOnOrderCount.set(0);
    this.fleetTotalDetails.set([]);

    const cached = this.cache.get<FleetCountResult>(this.fleetCountCacheKey);
    if (cached) {
      this.applyResult(cached);
      this.isLoading.set(false);
      return;
    }

    try {
      const result = await this.httpCancellation.singleValueFrom(
        this.mibpApiService.invoke(equipmentsFnGetEquipmentFleetCount, { body: { query: "", skip: 0, take: 0 } })
      );

      if (result) {
        this.cache.add(this.fleetCountCacheKey, result, this.fleetCountCacheDuration, CacheScope.UserStorage);
        this.applyResult(result);
      } else {
        this.fleetChartData.set(null);
      }
    } catch (error) {
      this.hasError.set(true);
      this.log.error("error Equipment Fleet Count", error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private applyResult(result: FleetCountResult): void {
    this.fleetActiveCount.set(result.active);
    this.fleetInActiveCount.set(result.inActive);
    this.fleetOnOrderCount.set(result.onOrder);
    const countWithoutDecommisioned = result.count - result.deCommisioned;
    this.fleetTotalDetails.set([countWithoutDecommisioned.toString(), this.fleetMachineText()?.toUpperCase() ?? '']);
    this.fleetChartData.set([
      { labelResourceString: "MyFleet_Active", value: result.active },
      { labelResourceString: "MyFleet_InActive", value: result.inActive },
      { labelResourceString: "MyFleet_OnOrder", value: result.onOrder },
    ] as BasicChartData[]);
  }


}
